/**
 * Replaces placeholders in a template string with values from a replacements object.
 *
 * This utility function provides a maintainable way to handle dynamic string
 * interpolation, especially for i18n strings with dynamic values.
 * Instead of hardcoding values like `$470` in components and using string
 * replace operations, use semantic placeholders like `{DYNAMIC_SERVICE_PLAN_FEE}`
 * in Contentful strings and interpolate them here.
 *
 * @param template - The template string containing placeholders in the format `{PLACEHOLDER_NAME}`.
 *                   If null or undefined (e.g., from missing i18n keys), returns an empty string.
 * @param replacements - An object mapping placeholder names to their replacement values
 * @returns The template string with all placeholders replaced by their corresponding values.
 *          If a placeholder exists in the template but has no replacement value, it remains unchanged.
 *          If the template is empty, null, or undefined, returns an empty string.
 *          Always returns a string (never null or undefined), safe for chaining string operations.
 *
 * @example
 * // Single placeholder
 * interpolateString(
 *   'Base Service Plan fees are {DYNAMIC_SERVICE_PLAN_FEE} per month',
 *   { DYNAMIC_SERVICE_PLAN_FEE: '$470' }
 * );
 * // Returns: 'Base Service Plan fees are $470 per month'
 *
 * @example
 * // Multiple placeholders
 * interpolateString(
 *   'The {ITEM_NAME} costs {PRICE} and has {QUANTITY} units in stock',
 *   { ITEM_NAME: 'Scanner', PRICE: '$1,200', QUANTITY: '5' }
 * );
 * // Returns: 'The Scanner costs $1,200 and has 5 units in stock'
 *
 * @example
 * // Numeric values are automatically converted to strings
 * interpolateString(
 *   'Discount: {DISCOUNT_PERCENT}% off',
 *   { DISCOUNT_PERCENT: 20 }
 * );
 * // Returns: 'Discount: 20% off'
 *
 * @example
 * // Nullish template handling (e.g., missing i18n key)
 * interpolateString(undefined, { DYNAMIC_SERVICE_PLAN_FEE: '$470' });
 * // Returns: ''
 * // Safe to chain: interpolateString(undefined, {...}).replace('* ', '') // No crash
 */
export function interpolateString(
  template: string | null | undefined,
  replacements: Record<string, string | number>
): string {
  if (!template) {
    return template ?? '';
  }

  let result = template;

  // Iterate through each replacement and apply it to the template
  Object.entries(replacements).forEach(([key, value]) => {
    // Create a regex pattern for the placeholder
    // Pattern: {PLACEHOLDER_NAME}
    const placeholder = new RegExp(`\\{${key}\\}`, 'g');
    result = result.replace(placeholder, String(value));
  });

  return result;
}
